import moment from 'moment'
import { SUBSCRIPTION_WARNING_THRESHOLDS } from '../../constants/subscriptionPlans'
import { formatTime, getDaysRemaining } from '../../utils/date.util'
import {
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline'

export default function SubscriptionDetails({ subscription }) {
  const isExpired = moment(subscription?.expiresAt).isBefore(moment(), 'day')

  const getStatusBadge = () => {
    if (isExpired) return
    if (!subscription || !subscription.isActive) {
      return (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-neutral-200-100 border border-gray-200">
          <XCircleIcon className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Not Subscribed</span>
        </div>
      )
    }

    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border ${
          isExpired
            ? 'bg-orange-50 border-orange-200 text-orange-800'
            : 'bg-green-50 border-green-200 text-green-800'
        }`}
      >
        <CheckCircleIcon className="w-4 h-4" />
        <span className="text-sm font-medium">{isExpired ? 'Expired' : 'Active'} </span>
      </div>
    )
  }

  const renderExpiresAt = () => {
    if (!subscription || !subscription.isActive || isExpired) {
      return 'Expired'
    }

    if (getDaysRemaining(subscription.expiresAt) <= SUBSCRIPTION_WARNING_THRESHOLDS.CRITICAL) {
      return 'Expires today'
    }

    if (getDaysRemaining(subscription.expiresAt) <= SUBSCRIPTION_WARNING_THRESHOLDS.WARNING) {
      return `Expires in ${getDaysRemaining(subscription.expiresAt)} days`
    }

    return `Expires on ${formatTime(subscription.expiresAt)}`
  }

  const renderSubscriptionInfo = () => {
    if (!subscription || !subscription.isActive) {
      return (
        <div className="text-center py-8">
          <XCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 text-sm">No active subscription</p>
        </div>
      )
    }

    const { pricePaidText, expiresAt, createdAt, subscriptionType } = subscription
    const daysRemaining = getDaysRemaining(expiresAt)

    const infoItems = [
      {
        icon: CreditCardIcon,
        label: 'Subscription Type',
        value: subscriptionType,
      },
      {
        icon: CreditCardIcon,
        label: 'Price Paid',
        value: pricePaidText,
      },
      {
        icon: ClockIcon,
        label: 'Subscribed On',
        value: formatTime(createdAt),
      },
      {
        icon: ClockIcon,
        label: 'Expires On',
        value: formatTime(expiresAt),
      },
    ]

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          {isExpired ? (
            <>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-sm font-medium text-gray-900">Subscription Expired</span>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-900">Subscription Active</span>
            </div>
          )}
          {getStatusBadge()}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {infoItems.map((item, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-neutral-200-50 rounded-lg">
              <item.icon className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {item.label}
                </dt>
                <dd className="text-sm font-medium text-gray-900 mt-1 break-words">{item.value}</dd>
              </div>
            </div>
          ))}
        </div>

        {daysRemaining !== null && (
          <div
            className={`p-4 rounded-lg border-l-4 ${
              daysRemaining <= SUBSCRIPTION_WARNING_THRESHOLDS.CRITICAL
                ? 'bg-orange-50 border-orange-400'
                : daysRemaining <= SUBSCRIPTION_WARNING_THRESHOLDS.WARNING
                ? 'bg-yellow-50 border-yellow-400'
                : 'bg-blue-50 border-blue-400'
            }`}
          >
            <div className="flex items-center">
              <ClockIcon className="w-5 h-5 text-gray-600 mr-2" />
              <span className="text-sm font-medium text-gray-900">{renderExpiresAt()}</span>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-background border border-gray-200 mt-6 rounded-lg overflow-hidden">
      <div className="px-6 py-4 bg-neutral-200-50 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Subscription Details</h3>
      </div>
      <div className="p-6">{renderSubscriptionInfo()}</div>
    </div>
  )
}
